/**
 * `/gallery/create-pool-tiles` draws the create pool wizard's step 1 tile
 * grids (ISSUE-1092) with fixed data, so a browser can measure them at 320,
 * 390 and 1440 wide in light and dark with no catalog or service behind them
 * (tests/e2e/src/app/createPoolTilesGeometry.e2e.ts). Signed in and linked
 * from no navigation, following the `/gallery/marks` precedent, and named
 * `gallery_.` for the same reason that file gives: `/gallery` renders no
 * Outlet.
 */
import { createFileRoute } from "@tanstack/react-router";
import { type ReactNode, useState } from "react";

import { requireSignedIn } from "../auth/requireSignedIn";
import { ContestTypeIcon } from "../features/createPool/contestTypeIcons";
import {
  CreatePoolTileGrid,
  type typeCreatePoolTile,
} from "../features/createPool/CreatePoolTileGrid";
import { SportIcon } from "../features/onboarding/sportIcons";

const GALLERY_LEAGUES: readonly {
  value: string;
  title: string;
  subtitle: string;
  sportKey: string;
}[] = [
  {
    value: "nfl",
    title: "NFL",
    subtitle: "National Football League",
    sportKey: "football",
  },
  {
    value: "nba",
    title: "NBA",
    subtitle: "National Basketball Association",
    sportKey: "basketball",
  },
  {
    value: "mlb",
    title: "MLB",
    subtitle: "Major League Baseball",
    sportKey: "baseball",
  },
  {
    value: "nhl",
    title: "NHL",
    subtitle: "National Hockey League",
    sportKey: "hockey",
  },
  {
    value: "wnba",
    title: "WNBA",
    subtitle: "Women's National Basketball Association",
    sportKey: "basketball",
  },
  {
    value: "mls",
    title: "MLS",
    subtitle: "Major League Soccer",
    sportKey: "soccer",
  },
  {
    value: "ncaa-fbs",
    title: "NCAA FBS",
    subtitle: "NCAA Football Bowl Subdivision",
    sportKey: "football",
  },
];

const LEAGUE_TILES: readonly typeCreatePoolTile[] = GALLERY_LEAGUES.map(
  (league) => ({
    value: league.value,
    title: league.title,
    subtitle: league.subtitle,
    icon: <SportIcon sportKey={league.sportKey} />,
    isDisabled: false,
  }),
);

const CONTEST_TILES: readonly typeCreatePoolTile[] = [
  {
    value: "survivor",
    title: "Survivor",
    subtitle: "Pick one team each week. Pick a loser and you're out.",
    icon: <ContestTypeIcon contestType="survivor" />,
    isDisabled: false,
  },
  {
    value: "squares",
    title: "Squares",
    subtitle: "Coming soon",
    icon: <ContestTypeIcon contestType="squares" />,
    isDisabled: true,
  },
  {
    value: "strip-card",
    title: "Strip Card",
    subtitle: "Coming soon",
    icon: <ContestTypeIcon contestType="strip-card" />,
    isDisabled: true,
  },
];

const CreatePoolTilesGalleryScreen = (): ReactNode => {
  const [league, setLeague] = useState<string | null>("nfl");
  const [contestType, setContestType] = useState<string | null>("survivor");
  return (
    <div
      data-testid="create-pool-tiles-gallery"
      className="flex flex-col gap-6 p-4"
    >
      <h1 className="text-h1 text-ink">Create pool tiles</h1>
      <CreatePoolTileGrid
        label="Which league?"
        testId="gallery-league-tiles"
        tiles={LEAGUE_TILES}
        value={league}
        onValueChange={setLeague}
      />
      <CreatePoolTileGrid
        label="What are you playing?"
        testId="gallery-contest-tiles"
        tiles={CONTEST_TILES}
        value={contestType}
        onValueChange={setContestType}
      />
    </div>
  );
};

export const Route = createFileRoute("/gallery_/create-pool-tiles")({
  beforeLoad: requireSignedIn,
  component: CreatePoolTilesGalleryScreen,
});
